import { Injectable } from '@angular/core';
import { StripeService } from '../stripe/stripe.service';
import {
  CompanyGroupProductOnboardingRow,
  CompanyGroupProductOnboardingStatus,
  TeamCompaniesService,
} from '../teamcompanies/teamcompanies.service';
import { TeamsService } from '../teams/teams.service';
import { VendorGroupsPlansService } from '../vendor-groups-plans/vendor-groups-plans.service';
import { VendorGroupsProductsService } from '../vendor-groups-products/vendor-groups-products.service';
import { VendorGroupsRatesService } from '../vendor-groups-rates/vendor-groups-rates.service';
import { VendorProductsService } from '../vendorproducts/vendorproducts.service';
import {
  BenefitCategoryTab,
  normalizeProductPayrollBenefitMap,
  ProductBenefitMapping,
} from './payroll-benefit-categories';
export type {
  BenefitCategoryTab,
  PayrollBenefitSubcategoryDefinition,
  PayrollBenefitSubcategoryDefinitionsByCategory,
  ProductBenefitMapping,
} from './payroll-benefit-categories';

export type ActiveProductPlanIdsMap = Record<string, string[]>;
export type ActiveProductPlanLabelsMap = Record<string, string[]>;
export type ActiveProductPlanSelectionsMap = Record<string, string[]>;
export type BenefitProductPlanSelectionMode = 'plan-id' | 'plan-label';

export interface BenefitProductPlanItem {
  id: string;
  selectionMode: BenefitProductPlanSelectionMode;
  selectionValues: string[];
  planIds: string[];
  planLabels: string[];
  name: string;
  description: string;
  detailsUrl: string;
  tier: string;
  coverageTiers?: string[];
  sequence: number | null;
}

export interface ProductPayrollDeductionConfig {
  deductionCode: string;
  deductionCodeDescription?: string;
}

export interface BenefitProductItem {
  id: string;
  stripeProductId?: string;
  vendorName: string;
  productName: string;
  description: string;
  imageUrl: string;
  detailsUrl: string;
  mapping: ProductBenefitMapping;
  plans: BenefitProductPlanItem[];
  alwaysActive?: boolean;
  payrollDeductionAllowed?: boolean;
  payrollDeductionEnabled?: boolean;
  payrollDeductionCode?: string;
  payrollDeductionCodeDescription?: string;
  requiresApproval?: boolean;
  approvalStatus?: CompanyGroupProductOnboardingStatus;
  approvalLabel?: string;
  approvalDisabledReason?: string;
  canRequestApproval?: boolean;
  allowanceControlEnabled?: boolean;
  allowanceEligible?: boolean;
}

export interface AssignableBenefitProductItem {
  id: string;
  vendorName: string;
  productName: string;
  label: string;
}

export interface BenefitProductsLoadResult {
  company: any;
  team?: any;
  companyProductIdsInOrder: string[];
  companyActiveIds: string[];
  baselineSelectedIds: string[];
  baselineSelectedPlanSelectionsMap: ActiveProductPlanSelectionsMap;
  benefitProducts: BenefitProductItem[];
  assignableProducts: AssignableBenefitProductItem[];
}

export interface CompanyProductPlanLevelsLoadResult {
  company: any;
  companyProductIdsInOrder: string[];
  benefitProducts: BenefitProductItem[];
  baselineSelectedPlanSelectionsMap: ActiveProductPlanSelectionsMap;
}

export interface AssignActiveProductsToTeamsResult {
  persistedIds: string[];
  metadataByTeamId: Record<string, any>;
}

export interface AssignActiveProductsToTeamsOptions {
  managedProductIds?: string[];
  fallbackActiveProductIds?: string[];
}

export interface PersistTeamActiveProductsResult {
  metadata: any;
  persistedIds: string[];
  persistedPlanSelectionsMap: ActiveProductPlanSelectionsMap;
}

export interface PersistTeamActiveProductsOptions {
  suppressTeamUpdateEmail?: boolean;
}

export interface PersistTeamBenefitSettingsOptions {
  persistActiveProducts?: boolean;
}

export interface LoadBenefitProductsForCompanyOptions {
  requireCompleteProducts?: boolean;
  includeStripeProductEnrichment?: boolean;
  company?: any;
  includeGroupProductEnrichment?: boolean;
}

interface VendorGroupProductRecord {
  id: string;
  vendorProductIds?: string[];
  vendorProductId?: string;
  disabled?: boolean;
  metadata?: any;
}

interface VendorGroupPlanRecord {
  id: string;
  vendorGroupProductIds?: string[];
  vendorGroupProductId?: string;
  disabled?: boolean;
  metadata?: any;
}

interface VendorGroupRateRecord {
  id: string;
  companyIds?: string[];
  vendorProductId?: string;
  disabled?: boolean;
  metadata?: any;
}

interface RateTableChoiceOption {
  label: string;
  values?: Record<string, any>;
}

interface RateTableChoiceCandidate {
  name: string;
  field: string;
  label: string;
  options: RateTableChoiceOption[];
}

interface PayrollAdminDisplayNames {
  vendorName: string;
  productName: string;
}

interface BenefitProductLoadOptions {
  requireCompleteProducts?: boolean;
  includeStripeProductEnrichment: boolean;
  loadVendorProductById: (productId: string) => Promise<any>;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollBenefitsAssignmentService {
  constructor(
    private teamCompaniesService: TeamCompaniesService,
    private teamsService: TeamsService,
    private vendorGroupsProductsService: VendorGroupsProductsService,
    private vendorGroupsPlansService: VendorGroupsPlansService,
    private vendorGroupsRatesService: VendorGroupsRatesService,
    private vendorProductsService: VendorProductsService,
    private stripeService: StripeService,
  ) {}

  async loadBenefitProductsForCompany(
    companyId: string,
    teamId?: string,
    options: LoadBenefitProductsForCompanyOptions = {},
  ): Promise<BenefitProductsLoadResult> {
    const [company, team] = await Promise.all([
      options.company
        ? Promise.resolve(options.company)
        : this.teamCompaniesService.getCompany(companyId),
      teamId ? this.teamsService.getTeamInfo(teamId) : Promise.resolve(undefined),
    ]);
    const loadOptions: BenefitProductLoadOptions = {
      includeStripeProductEnrichment: options.includeStripeProductEnrichment !== false,
      requireCompleteProducts: options.requireCompleteProducts,
      loadVendorProductById: this.createVendorProductRequestCache(),
    };

    const companyPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
    const rawCompanyPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(company);
    const companyProductIdsInOrder: string[] = Array.isArray(companyPayrollConfig.products)
      ? companyPayrollConfig.products
      : [];
    const alwaysActiveProductIds = this.teamCompaniesService.getAlwaysActiveProductIds(company);

    const normalizedMap = this.normalizeProductPayrollBenefitMap(
      companyPayrollConfig.productPayrollBenefitCategoriesMap || {}
    );
    const normalizedPayrollDeductionMap = this.normalizeProductPayrollDeductionMap(
      companyPayrollConfig.productPayrollDeductionMap || {}
    );
    const payrollDeductionDisabledProductIds = new Set(
      this.teamCompaniesService.getPayrollDeductionDisabledProductIds(company)
    );

    const companyActiveIds: string[] = Array.isArray(rawCompanyPayrollConfig.activeProducts)
      ? rawCompanyPayrollConfig.activeProducts
      : [];

    const teamActiveIds: string[] = Array.isArray(team?.metadata?.activeProducts)
      ? team.metadata.activeProducts
      : [];

    const usingTeamOverride = team?.metadata?.activeProductsOverride === true || teamActiveIds.length > 0;
    const baselineSelectedIds = this.getPersistedSelectedProductIds(
      usingTeamOverride ? teamActiveIds : companyActiveIds,
      companyProductIdsInOrder,
      alwaysActiveProductIds,
    );
    const assignableProductIds = this.getPersistedSelectedProductIds(
      teamActiveIds.length > 0 ? teamActiveIds : companyActiveIds,
      companyProductIdsInOrder,
      alwaysActiveProductIds,
    );

    const mappedProductIds = companyProductIdsInOrder.filter(id => !!normalizedMap[id]?.category);
    const approvalRequiredProductIds = new Set(
      mappedProductIds.filter((productId) => normalizedMap[productId]?.requiresApproval === true)
    );
    const includeGroupProductEnrichment = options.includeGroupProductEnrichment !== false;
    const companyLineage = includeGroupProductEnrichment
      ? await this.getCompanyLineage(company)
      : [company];
    const [allVendorGroupProducts, allVendorGroupPlans, allVendorGroupRates, onboardingRows] = await Promise.all([
      includeGroupProductEnrichment
        ? this.vendorGroupsProductsService.getAllVendorGroupProducts().catch(() => [])
        : Promise.resolve([]),
      includeGroupProductEnrichment
        ? this.vendorGroupsPlansService.getAllVendorGroupPlans().catch(() => [])
        : Promise.resolve([]),
      includeGroupProductEnrichment
        ? this.vendorGroupsRatesService.getAllVendorGroupRates().catch(() => [])
        : Promise.resolve([]),
      this.teamCompaniesService.getGroupProductOnboardingStatuses(companyId).catch(() => []),
    ]);
    const benefitPlansByProductId = this.buildBenefitProductPlansByProductId(
      mappedProductIds,
      companyLineage.map((lineageCompany) => `${lineageCompany?.id || ''}`.trim()).filter((id) => !!id),
      allVendorGroupProducts,
      allVendorGroupPlans,
      allVendorGroupRates,
    );
    const disabledGroupProductIds = new Set(
      (onboardingRows || [])
        .filter((row) => approvalRequiredProductIds.has(`${row?.requestedVendorProductId || ''}`.trim()))
        .filter((row) => row?.status !== 'ready')
        .map((row) => `${row?.requestedVendorProductId || ''}`.trim())
        .filter((id) => !!id)
    );
    const enabledAlwaysActiveProductIds = alwaysActiveProductIds.filter(
      (productId) => companyProductIdsInOrder.includes(productId) && !disabledGroupProductIds.has(productId)
    );
    const filteredCompanyActiveIds = companyActiveIds.filter((productId) => !disabledGroupProductIds.has(productId));
    const filteredBaselineSelectedIds = this.getPersistedSelectedProductIds(
      baselineSelectedIds.filter((productId) => !disabledGroupProductIds.has(productId)),
      companyProductIdsInOrder,
      enabledAlwaysActiveProductIds,
    );
    const filteredAssignableProductIds = this.getPersistedSelectedProductIds(
      assignableProductIds.filter((productId) => !disabledGroupProductIds.has(productId)),
      companyProductIdsInOrder,
      enabledAlwaysActiveProductIds,
    );

    const alwaysActiveProductIdSet = new Set(enabledAlwaysActiveProductIds);
    const loadedProducts: Array<BenefitProductItem | null> = await Promise.all(
      mappedProductIds.map(async (productId) => {
        const mapping = normalizedMap[productId];
        if (!mapping) {
          return null;
        }
        const product = await this.loadBenefitProduct(
          productId,
          mapping,
          benefitPlansByProductId[productId] || [],
          payrollDeductionDisabledProductIds.has(productId) ? null : normalizedPayrollDeductionMap[productId] || null,
          !payrollDeductionDisabledProductIds.has(productId),
          allVendorGroupProducts,
          onboardingRows,
          loadOptions,
        );
        if (!product) {
          return null;
        }
        return {
          ...product,
          alwaysActive: alwaysActiveProductIdSet.has(productId),
        };
      })
    );

    const loadedAssignableProducts = await Promise.all(
      filteredAssignableProductIds.map((productId) => this.loadAssignableProduct(productId, loadOptions))
    );

    return {
      company,
      team,
      companyProductIdsInOrder,
      companyActiveIds: filteredCompanyActiveIds,
      baselineSelectedIds: filteredBaselineSelectedIds,
      baselineSelectedPlanSelectionsMap: {},
      benefitProducts: loadedProducts.filter((product): product is BenefitProductItem => !!product),
      assignableProducts: loadedAssignableProducts.filter(
        (product): product is AssignableBenefitProductItem => !!product
      ),
    };
  }

  async loadProductPlanSettingsForCompany(companyId: string): Promise<CompanyProductPlanLevelsLoadResult> {
    const company = await this.teamCompaniesService.getCompany(companyId);
    const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
    const companyProductIdsInOrder: string[] = Array.isArray(resolvedPayrollConfig?.products)
      ? resolvedPayrollConfig.products
      : [];
    const companyLineage = await this.getCompanyLineage(company);
    const [allVendorGroupProducts, allVendorGroupPlans, allVendorGroupRates] = await Promise.all([
      this.vendorGroupsProductsService.getAllVendorGroupProducts().catch(() => []),
      this.vendorGroupsPlansService.getAllVendorGroupPlans().catch(() => []),
      this.vendorGroupsRatesService.getAllVendorGroupRates().catch(() => []),
    ]);
    const benefitPlansByProductId = this.buildBenefitProductPlansByProductId(
      companyProductIdsInOrder,
      companyLineage.map((lineageCompany) => `${lineageCompany?.id || ''}`.trim()).filter((id) => !!id),
      allVendorGroupProducts,
      allVendorGroupPlans,
      allVendorGroupRates,
    );
    const configurableProductIds = companyProductIdsInOrder
      .filter((productId) => (benefitPlansByProductId[productId] || []).length > 0);
    const normalizedMap = this.normalizeProductPayrollBenefitMap(
      resolvedPayrollConfig?.productPayrollBenefitCategoriesMap || {}
    );
    const normalizedPayrollDeductionMap = this.normalizeProductPayrollDeductionMap(
      resolvedPayrollConfig?.productPayrollDeductionMap || {}
    );
    const loadedProducts = await Promise.all(
      configurableProductIds.map((productId) => this.loadBenefitProduct(
        productId,
        normalizedMap[productId] || { category: 'other' },
        benefitPlansByProductId[productId] || [],
        normalizedPayrollDeductionMap[productId] || null,
      ))
    );
    const baselineSelection = this.buildBaselineBenefitSelection(
      configurableProductIds,
      benefitPlansByProductId,
      resolvedPayrollConfig?.productPlanIdsMap,
      resolvedPayrollConfig?.productPlanLabelsMap,
    );

    return {
      company,
      companyProductIdsInOrder,
      benefitProducts: loadedProducts.filter((product): product is BenefitProductItem => !!product),
      baselineSelectedPlanSelectionsMap: baselineSelection.selectedPlanSelectionsMap,
    };
  }

  async assignActiveProductsToTeams(
    teamIds: string[],
    selectedProductIds: string[],
    companyProductIdsInOrder: string[],
    metadataByTeamId: Record<string, any> = {},
    alwaysActiveProductIds: string[] = [],
    options: AssignActiveProductsToTeamsOptions = {},
  ): Promise<AssignActiveProductsToTeamsResult> {
    const persistedIds = this.getPersistedSelectedProductIds(
      selectedProductIds,
      companyProductIdsInOrder,
      alwaysActiveProductIds,
    );
    const nextMetadataByTeamId: Record<string, any> = {};

    await Promise.all(
      teamIds.map(async (teamId) => {
        const currentMetadata = metadataByTeamId[teamId] || {};
        const managedProductIdSet = new Set(this.normalizeStringArray(options.managedProductIds || []));
        const currentActiveProductIds = Array.isArray(currentMetadata.activeProducts)
          ? currentMetadata.activeProducts
          : [];
        const effectiveCurrentProductIds = currentMetadata.activeProductsOverride === true || currentActiveProductIds.length > 0
          ? currentActiveProductIds
          : options.fallbackActiveProductIds || [];
        const preservedProductIds = managedProductIdSet.size > 0
          ? this.normalizeStringArray(effectiveCurrentProductIds)
            .filter((productId) => !managedProductIdSet.has(productId))
          : [];
        const teamPersistedIds = preservedProductIds.length > 0
          ? this.getPersistedSelectedProductIds(
            [...persistedIds, ...preservedProductIds],
            companyProductIdsInOrder,
            alwaysActiveProductIds,
          )
          : persistedIds;
        const nextMetadata = this.buildNextTeamBenefitMetadata(
          currentMetadata,
          teamPersistedIds,
        );
        nextMetadataByTeamId[teamId] = nextMetadata;
        await this.teamsService.modifyTeam(teamId, { metadata: nextMetadata });
      })
    );

    return {
      persistedIds,
      metadataByTeamId: nextMetadataByTeamId,
    };
  }

  async persistTeamActiveProducts(
    teamId: string,
    teamInfo: any,
    selectedProductIds: string[],
    companyProductIdsInOrder: string[],
    alwaysActiveProductIds: string[] = [],
    options: PersistTeamActiveProductsOptions = {},
  ): Promise<PersistTeamActiveProductsResult> {
    const persistedIds = this.getPersistedSelectedProductIds(
      selectedProductIds,
      companyProductIdsInOrder,
      alwaysActiveProductIds,
    );
    const nextMetadata = this.buildNextTeamBenefitMetadata(
      teamInfo?.metadata || {},
      persistedIds,
    );

    await this.teamsService.modifyTeam(teamId, {
      metadata: nextMetadata,
      ...(options.suppressTeamUpdateEmail ? { noEmail: true } : {}),
    });

    return {
      metadata: nextMetadata,
      persistedIds,
      persistedPlanSelectionsMap: {},
    };
  }

  async persistTeamBenefitSettings(
    teamId: string,
    teamInfo: any,
    selectedProductIds: string[],
    companyProductIdsInOrder: string[],
    alwaysActiveProductIds: string[],
    allowanceEligibleProductIds: string[],
    options: PersistTeamBenefitSettingsOptions = {},
  ): Promise<PersistTeamActiveProductsResult> {
    const persistedIds = this.getPersistedSelectedProductIds(
      selectedProductIds,
      companyProductIdsInOrder,
      alwaysActiveProductIds,
    );
    const existingMetadata = teamInfo?.metadata || {};
    const nextMetadata = options.persistActiveProducts
      ? this.buildNextTeamBenefitMetadata(existingMetadata, persistedIds)
      : { ...existingMetadata };
    nextMetadata.allowanceEligibleProductIds = this.normalizeStringArray(allowanceEligibleProductIds)
      .filter((productId) => companyProductIdsInOrder.includes(productId));

    await this.teamsService.modifyTeam(teamId, { metadata: nextMetadata });

    return {
      metadata: nextMetadata,
      persistedIds,
      persistedPlanSelectionsMap: {},
    };
  }

  getPersistedSelectedProductIds(
    selectedIds: string[],
    companyProductIdsInOrder: string[],
    alwaysActiveProductIds: string[] = [],
  ): string[] {
    const selectedIdSet = new Set(this.normalizeStringArray(selectedIds));
    const combinedIds = this.normalizeStringArray([
      ...alwaysActiveProductIds,
      ...selectedIds,
    ]).filter((productId) => companyProductIdsInOrder.includes(productId));

    return [...combinedIds].sort((a, b) => {
      const ai = companyProductIdsInOrder.indexOf(a);
      const bi = companyProductIdsInOrder.indexOf(b);
      if (ai !== bi) {
        return ai - bi;
      }

      const aIsAlwaysActive = !selectedIdSet.has(a);
      const bIsAlwaysActive = !selectedIdSet.has(b);
      if (aIsAlwaysActive !== bIsAlwaysActive) {
        return aIsAlwaysActive ? -1 : 1;
      }

      return a.localeCompare(b);
    });
  }

  getSelectedMappedBenefitProductIds(companyOrPayrollConfig: any, team?: any): string[] {
    const companyConfigSource = this.toCompanyPayrollConfigSource(companyOrPayrollConfig);
    const payrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(companyConfigSource);
    const rawPayrollConfig = this.teamCompaniesService.getRawPayrollConfig(companyConfigSource);
    const companyProductIdsInOrder: string[] = Array.isArray(payrollConfig.products)
      ? payrollConfig.products
      : [];
    const alwaysActiveProductIds = this.teamCompaniesService.getAlwaysActiveProductIds(companyConfigSource);
    const normalizedMap = this.normalizeProductPayrollBenefitMap(
      payrollConfig.productPayrollBenefitCategoriesMap || {}
    );
    const mappedProductIds = new Set(
      companyProductIdsInOrder.filter((productId) => !!normalizedMap[productId]?.category)
    );

    const companyActiveIds: string[] = Array.isArray(rawPayrollConfig.activeProducts)
      ? rawPayrollConfig.activeProducts
      : [];
    const teamActiveIds: string[] = Array.isArray(team?.metadata?.activeProducts)
      ? team.metadata.activeProducts
      : [];
    const usingTeamOverride = team?.metadata?.activeProductsOverride === true || teamActiveIds.length > 0;
    const groupProductOnboardingRows = this.getGroupProductOnboardingRows(companyConfigSource, rawPayrollConfig);
    const disabledGroupProductIds = new Set(
      groupProductOnboardingRows
        .filter((row: any) => normalizedMap[`${row?.requestedVendorProductId || ''}`.trim()]?.requiresApproval === true)
        .filter((row: any) => `${row?.status || ''}`.trim().toLowerCase() !== 'ready')
        .map((row: any) => `${row?.requestedVendorProductId || ''}`.trim())
        .filter((productId: string) => !!productId)
    );
    const selectedIds = (usingTeamOverride ? teamActiveIds : companyActiveIds)
      .filter((productId: string) => mappedProductIds.has(productId) && !disabledGroupProductIds.has(productId));

    const enabledAlwaysActiveProductIds = alwaysActiveProductIds.filter(
      (productId) => mappedProductIds.has(productId) && !disabledGroupProductIds.has(productId)
    );

    return this.getPersistedSelectedProductIds(
      [...new Set(selectedIds)],
      companyProductIdsInOrder,
      enabledAlwaysActiveProductIds,
    );
  }

  getAlwaysActiveBenefitProductIds(benefitProducts: BenefitProductItem[]): string[] {
    return this.normalizeStringArray(
      (benefitProducts || [])
        .filter((product) => product?.alwaysActive === true)
        .map((product) => product.id)
    );
  }

  private toCompanyPayrollConfigSource(companyOrPayrollConfig: any): any {
    if (
      companyOrPayrollConfig
      && typeof companyOrPayrollConfig === 'object'
      && (
        Object.prototype.hasOwnProperty.call(companyOrPayrollConfig, 'payrollConfig')
        || Object.prototype.hasOwnProperty.call(companyOrPayrollConfig, 'resolvedPayrollConfig')
      )
    ) {
      return companyOrPayrollConfig;
    }

    return {
      payrollConfig: companyOrPayrollConfig,
      resolvedPayrollConfig: companyOrPayrollConfig,
    };
  }

  private getGroupProductOnboardingRows(
    companyConfigSource: any,
    rawPayrollConfig: any,
  ): CompanyGroupProductOnboardingRow[] {
    if (companyConfigSource && typeof companyConfigSource === 'object') {
      if (Object.prototype.hasOwnProperty.call(companyConfigSource, 'groupProductOnboardingRows')) {
        return Array.isArray(companyConfigSource.groupProductOnboardingRows)
          ? companyConfigSource.groupProductOnboardingRows
          : [];
      }

      if (Object.prototype.hasOwnProperty.call(companyConfigSource, 'groupProductOnboarding')) {
        return Array.isArray(companyConfigSource.groupProductOnboarding)
          ? companyConfigSource.groupProductOnboarding
          : [];
      }
    }

    return Array.isArray(rawPayrollConfig?.groupProductOnboarding)
      ? rawPayrollConfig.groupProductOnboarding
      : [];
  }

  normalizeActiveProductPlanSelectionsMap(map: Record<string, any>): ActiveProductPlanSelectionsMap {
    const normalized: ActiveProductPlanSelectionsMap = {};
    Object.entries(map || {}).forEach(([productId, planIds]) => {
      const normalizedProductId = `${productId || ''}`.trim();
      if (!normalizedProductId || !Array.isArray(planIds)) {
        return;
      }
      const normalizedPlanIds = this.normalizeStringArray(planIds);
      if (normalizedPlanIds.length > 0) {
        normalized[normalizedProductId] = normalizedPlanIds;
      }
    });
    return normalized;
  }

  normalizeActiveProductPlanIdsMap(map: Record<string, any>): ActiveProductPlanSelectionsMap {
    return this.normalizeActiveProductPlanSelectionsMap(map);
  }

  buildCompanyProductPlanRestrictionMaps(
    selectedPlanSelectionsByProductId: ActiveProductPlanSelectionsMap,
    benefitProducts: BenefitProductItem[],
  ): {
    planIdsMap: ActiveProductPlanIdsMap;
    planLabelsMap: ActiveProductPlanLabelsMap;
  } {
    const normalizedPlanSelectionsMap =
      this.normalizeActiveProductPlanSelectionsMap(selectedPlanSelectionsByProductId);
    const planIdsMap: ActiveProductPlanIdsMap = {};
    const planLabelsMap: ActiveProductPlanLabelsMap = {};

    benefitProducts.forEach((product) => {
      if (!product?.plans.length) {
        return;
      }

      const selectionMode = this.getBenefitProductPlanSelectionMode(product.plans);
      const availableSelectionValues = this.getBenefitProductAvailablePlanSelectionValues(product.plans);
      const selectedSelectionValues = (normalizedPlanSelectionsMap[product.id] || [])
        .filter((selectionValue) => availableSelectionValues.includes(selectionValue));

      if (
        !selectionMode
        || selectedSelectionValues.length === 0
        || selectedSelectionValues.length === availableSelectionValues.length
      ) {
        return;
      }

      const sortedSelectionValues = this.sortPlanSelectionValuesByAvailableOrder(
        selectedSelectionValues,
        availableSelectionValues,
      );
      if (selectionMode === 'plan-label') {
        planLabelsMap[product.id] = sortedSelectionValues;
      } else {
        planIdsMap[product.id] = sortedSelectionValues;
      }
    });

    return {
      planIdsMap,
      planLabelsMap,
    };
  }

  normalizeProductPayrollBenefitMap(map: Record<string, any>): Record<string, ProductBenefitMapping> {
    return normalizeProductPayrollBenefitMap(map);
  }

  normalizeProductPayrollDeductionMap(map: Record<string, any>): Record<string, ProductPayrollDeductionConfig> {
    const normalized: Record<string, ProductPayrollDeductionConfig> = {};

    Object.entries(map || {}).forEach(([productId, config]) => {
      const normalizedProductId = `${productId || ''}`.trim();
      const deductionCode = `${config?.deductionCode || ''}`.trim();
      const deductionCodeDescription = `${config?.deductionCodeDescription || ''}`.trim();
      if (!normalizedProductId || !deductionCode) {
        return;
      }

      normalized[normalizedProductId] = deductionCodeDescription
        ? { deductionCode, deductionCodeDescription }
        : { deductionCode };
    });

    return normalized;
  }

  buildProductPayrollDeductionMap(
    benefitProducts: BenefitProductItem[],
  ): Record<string, ProductPayrollDeductionConfig> {
    const normalized: Record<string, ProductPayrollDeductionConfig> = {};

    (benefitProducts || []).forEach((product) => {
      const normalizedProductId = `${product?.id || ''}`.trim();
      const deductionCode = `${product?.payrollDeductionCode || ''}`.trim();
      const deductionCodeDescription = `${product?.payrollDeductionCodeDescription || ''}`.trim();
      if (!normalizedProductId || product?.payrollDeductionEnabled !== true || !deductionCode) {
        return;
      }

      normalized[normalizedProductId] = deductionCodeDescription
        ? { deductionCode, deductionCodeDescription }
        : { deductionCode };
    });

    return normalized;
  }

  private buildBenefitProductPlansByProductId(
    mappedProductIds: string[],
    companyLineageIds: string[],
    vendorGroupProducts: VendorGroupProductRecord[],
    vendorGroupPlans: VendorGroupPlanRecord[],
    vendorGroupRates: VendorGroupRateRecord[],
  ): Record<string, BenefitProductPlanItem[]> {
    const relevantProductIds = new Set(mappedProductIds);
    const vendorGroupProductIdsByProductId = new Map<string, Set<string>>();
    const plansByProductId = mappedProductIds.reduce((acc: Record<string, BenefitProductPlanItem[]>, productId) => {
      acc[productId] = [];
      return acc;
    }, {});
    const matchedPlansByProductId = mappedProductIds.reduce((acc: Record<string, VendorGroupPlanRecord[]>, productId) => {
      acc[productId] = [];
      return acc;
    }, {});

    (vendorGroupProducts || []).forEach((groupProduct) => {
      if (!groupProduct?.id || groupProduct?.disabled || groupProduct?.metadata?.disabled) {
        return;
      }
      const vendorProductIds = this.normalizeStringArray(
        Array.isArray(groupProduct?.vendorProductIds)
          ? groupProduct.vendorProductIds
          : [groupProduct?.vendorProductId]
      ).filter((productId) => relevantProductIds.has(productId));
      if (!vendorProductIds.length) {
        return;
      }

      vendorProductIds.forEach((productId) => {
        if (!vendorGroupProductIdsByProductId.has(productId)) {
          vendorGroupProductIdsByProductId.set(productId, new Set<string>());
        }
        vendorGroupProductIdsByProductId.get(productId)?.add(`${groupProduct.id}`);
      });
    });

    (vendorGroupPlans || []).forEach((plan) => {
      if (!plan?.id || plan?.disabled || plan?.metadata?.disabled) {
        return;
      }
      const vendorGroupProductIds = new Set(this.normalizeStringArray(
        Array.isArray(plan?.vendorGroupProductIds)
          ? plan.vendorGroupProductIds
          : [plan?.vendorGroupProductId]
      ));
      if (!vendorGroupProductIds.size) {
        return;
      }

      mappedProductIds.forEach((productId) => {
        const matchingGroupProductIds = vendorGroupProductIdsByProductId.get(productId);
        if (!matchingGroupProductIds) {
          return;
        }
        const matchesProduct = Array.from(vendorGroupProductIds).some((groupProductId) => (
          matchingGroupProductIds.has(groupProductId)
        ));
        if (!matchesProduct) {
          return;
        }
        matchedPlansByProductId[productId].push(plan);
      });
    });

    Object.entries(matchedPlansByProductId).forEach(([productId, matchedPlans]) => {
      const groupedPlans = new Map<string, VendorGroupPlanRecord[]>();
      (matchedPlans || []).forEach((plan) => {
        const groupingKey = this.getBenefitPlanGroupingKey(plan);
        if (!groupingKey) {
          return;
        }
        if (!groupedPlans.has(groupingKey)) {
          groupedPlans.set(groupingKey, []);
        }
        groupedPlans.get(groupingKey)?.push(plan);
      });

      const groupedPlanItems = Array.from(groupedPlans.values())
        .map((planGroup) => this.toBenefitProductPlanItem(planGroup))
        .filter((plan): plan is BenefitProductPlanItem => !!plan);
      const matchingVendorGroupProductIds = Array.from(vendorGroupProductIdsByProductId.get(productId) || []);
      const matchedRate = this.getBestMatchingVendorGroupRate(
        vendorGroupRates,
        matchingVendorGroupProductIds,
        companyLineageIds,
      );
      const rateDerivedPlanItems = this.buildRateDerivedBenefitProductPlanItems(
        productId,
        matchedRate?.metadata,
        groupedPlanItems,
      );

      if (rateDerivedPlanItems.length > 1) {
        plansByProductId[productId] = rateDerivedPlanItems;
        return;
      }

      if (rateDerivedPlanItems.length === 1) {
        plansByProductId[productId] = [];
        return;
      }

      plansByProductId[productId] = groupedPlanItems.length > 1 ? groupedPlanItems : [];
    });

    Object.values(plansByProductId).forEach((plans) => {
      plans.sort((left, right) => {
        const leftSequence = left.sequence ?? Number.MAX_SAFE_INTEGER;
        const rightSequence = right.sequence ?? Number.MAX_SAFE_INTEGER;
        if (leftSequence !== rightSequence) {
          return leftSequence - rightSequence;
        }
        return left.name.localeCompare(right.name);
      });
    });

    return plansByProductId;
  }

  private buildBaselineBenefitSelection(
    baselineSelectedIds: string[],
    benefitPlansByProductId: Record<string, BenefitProductPlanItem[]>,
    rawActiveProductPlanIdsMap: Record<string, any> = {},
    rawActiveProductPlanLabelsMap: Record<string, any> = {},
  ): { productIds: string[]; selectedPlanSelectionsMap: ActiveProductPlanSelectionsMap } {
    const normalizedPlanIdsMap = this.normalizeActiveProductPlanSelectionsMap(rawActiveProductPlanIdsMap);
    const normalizedPlanLabelsMap = this.normalizeActiveProductPlanSelectionsMap(rawActiveProductPlanLabelsMap);
    const selectedPlanSelectionsMap: ActiveProductPlanSelectionsMap = {};
    const selectedProductIds: string[] = [];

    baselineSelectedIds.forEach((productId) => {
      if (selectedProductIds.includes(productId)) {
        return;
      }
      const plans = benefitPlansByProductId[productId] || [];
      const availableSelectionValues = this.getBenefitProductAvailablePlanSelectionValues(plans);
      if (availableSelectionValues.length === 0) {
        selectedProductIds.push(productId);
        return;
      }

      const selectionMode = this.getBenefitProductPlanSelectionMode(plans);
      const normalizedSelectionsMap = selectionMode === 'plan-label'
        ? normalizedPlanLabelsMap
        : normalizedPlanIdsMap;

      if (selectionMode && Object.prototype.hasOwnProperty.call(normalizedSelectionsMap, productId)) {
        const explicitSelectionValues = plans.reduce((allSelections: string[], plan) => {
          const planSelectionValues = this.getBenefitProductPlanSelectionValues(plan);
          const hasAnySelectedValue = planSelectionValues
            .some((selectionValue) => normalizedSelectionsMap[productId].includes(selectionValue));
          return hasAnySelectedValue ? allSelections.concat(planSelectionValues) : allSelections;
        }, []);
        if (explicitSelectionValues.length === 0) {
          return;
        }
        selectedProductIds.push(productId);
        selectedPlanSelectionsMap[productId] = this.sortPlanSelectionValuesByAvailableOrder(
          explicitSelectionValues,
          availableSelectionValues,
        );
        return;
      }

      selectedProductIds.push(productId);
      selectedPlanSelectionsMap[productId] = [...availableSelectionValues];
    });

    return {
      productIds: selectedProductIds,
      selectedPlanSelectionsMap,
    };
  }

  private buildNextTeamBenefitMetadata(
    existingMetadata: any,
    persistedIds: string[],
  ): any {
    const nextMetadata = {
      ...(existingMetadata || {}),
      activeProducts: persistedIds,
      activeProductsOverride: true,
    };
    delete nextMetadata.activeProductPlanIdsMap;
    delete nextMetadata.activeProductPlanLabelsMap;

    return nextMetadata;
  }

  private getBenefitPlanGroupingKey(plan: VendorGroupPlanRecord): string | null {
    const metadata = plan?.metadata || {};
    const tierLabel = this.getBenefitPlanTierLabel(`${metadata?.tier || ''}`.trim()).toLowerCase();
    const meaningfulName = [
      metadata?.name,
      metadata?.displayName,
      metadata?.planName,
      metadata?.shortName,
      metadata?.label,
    ]
      .map((value) => `${value || ''}`.trim())
      .find((value) => !!value);

    if (!meaningfulName) {
      return null;
    }

    return meaningfulName.toLowerCase() === tierLabel ? null : meaningfulName.toLowerCase();
  }

  private toBenefitProductPlanItem(plans: VendorGroupPlanRecord[]): BenefitProductPlanItem | null {
    const firstPlan = plans[0];
    const firstPlanMetadata = firstPlan?.metadata || {};
    const tier = `${firstPlanMetadata?.tier || ''}`.trim();
    const name = [
      firstPlanMetadata?.name,
      firstPlanMetadata?.displayName,
      firstPlanMetadata?.planName,
      firstPlanMetadata?.shortName,
      firstPlanMetadata?.label,
    ]
      .map((value) => `${value || ''}`.trim())
      .find((value) => !!value);

    if (!name) {
      return null;
    }

    const description = plans
      .reduce((values: string[], plan) => values.concat([
        plan?.metadata?.description,
        plan?.metadata?.planDescription,
        plan?.metadata?.subtitle,
      ] as string[]), [])
      .map((value) => `${value || ''}`.trim())
      .find((value) => !!value) || '';
    const detailsUrl = plans
      .map((plan) => `${plan?.metadata?.detailsUrl || ''}`.trim())
      .find((value) => !!value) || '';
    const parsedSequences = plans
      .map((plan) => Number(plan?.metadata?.sequence))
      .filter((value) => Number.isFinite(value));
    const planIds = this.normalizeStringArray(plans.map((plan) => plan?.id));
    const coverageTiers = this.normalizeStringArray(plans.map((plan) => plan?.metadata?.tier));

    if (!planIds.length) {
      return null;
    }

    return {
      id: planIds[0],
      selectionMode: 'plan-id',
      selectionValues: [...planIds],
      planIds,
      planLabels: [],
      name,
      description,
      detailsUrl,
      tier,
      coverageTiers,
      sequence: parsedSequences.length ? Math.min(...parsedSequences) : null,
    };
  }

  private sortPlanSelectionValuesByAvailableOrder(
    selectedSelectionValues: string[],
    availableSelectionValues: string[],
  ): string[] {
    return [...new Set(selectedSelectionValues)].sort((left, right) => {
      const leftIndex = availableSelectionValues.indexOf(left);
      const rightIndex = availableSelectionValues.indexOf(right);
      if (leftIndex === -1 && rightIndex === -1) {
        return left.localeCompare(right);
      }
      if (leftIndex === -1) {
        return 1;
      }
      if (rightIndex === -1) {
        return -1;
      }
      return leftIndex - rightIndex;
    });
  }

  private normalizeStringArray(values: any[]): string[] {
    return [...new Set((values || [])
      .map((value) => `${value || ''}`.trim())
      .filter((value) => !!value))];
  }

  private getBenefitProductAvailablePlanSelectionValues(plans: BenefitProductPlanItem[]): string[] {
    return plans.reduce(
      (allSelectionValues: string[], plan) => allSelectionValues.concat(this.getBenefitProductPlanSelectionValues(plan)),
      [],
    );
  }

  private getBenefitProductPlanSelectionMode(plans: BenefitProductPlanItem[]): BenefitProductPlanSelectionMode | null {
    const uniqueModes = [...new Set(
      plans
        .map((plan) => plan.selectionMode)
        .filter((selectionMode): selectionMode is BenefitProductPlanSelectionMode => !!selectionMode)
    )];
    return uniqueModes.length === 1 ? uniqueModes[0] : null;
  }

  private getBenefitProductPlanSelectionValues(plan: BenefitProductPlanItem): string[] {
    if (plan.selectionMode === 'plan-label') {
      return [...plan.planLabels];
    }
    return [...plan.planIds];
  }

  private getBenefitPlanTierLabel(tier: string): string {
    switch (`${tier || ''}`.trim().toUpperCase()) {
      case 'EMPL':
        return 'Member Only';
      case 'SPSE':
        return 'Member plus Spouse';
      case 'SPF':
        return 'Member plus Children';
      case 'DPDT':
        return 'Member plus Family';
      case 'PLUS1':
        return 'Member plus One';
      default:
        return tier;
    }
  }

  private async loadBenefitProduct(
    productId: string,
    mapping: ProductBenefitMapping,
    plans: BenefitProductPlanItem[],
    payrollDeductionConfig: ProductPayrollDeductionConfig | null,
    payrollDeductionAllowed = true,
    vendorGroupProducts: VendorGroupProductRecord[] = [],
    onboardingRows: CompanyGroupProductOnboardingRow[] = [],
    options: BenefitProductLoadOptions = {
      includeStripeProductEnrichment: true,
      loadVendorProductById: (id: string) => this.vendorProductsService.getVendorProductById(id),
    },
  ): Promise<BenefitProductItem | null> {
    try {
      const vendorProduct: any = await options.loadVendorProductById(productId);
      let description = '';
      let stripeProduct: any;
      const stripeProductId = vendorProduct?.stripeProductId || vendorProduct?.productId;

      if (options.includeStripeProductEnrichment && stripeProductId) {
        try {
          stripeProduct = await this.stripeService.getProduct(stripeProductId);
          description = stripeProduct?.description || '';
        } catch {
          // Ignore stripe enrichment failures and still render base vendor product details.
        }
      }

      const imageUrl = this.resolveBenefitProductImageUrl(vendorProduct, stripeProduct);

      const matchingGroupProduct = (vendorGroupProducts || []).find((groupProduct) => {
        const vendorProductIds = this.normalizeStringArray(
          Array.isArray(groupProduct?.vendorProductIds)
            ? groupProduct.vendorProductIds
            : [groupProduct?.vendorProductId]
        );
        return vendorProductIds.includes(productId);
      });
      const onboardingRow = (onboardingRows || []).find((row) => row?.requestedVendorProductId === productId);
      const requiresApproval = mapping?.requiresApproval === true;
      const displayNames = this.getPayrollAdminDisplayNames(vendorProduct);
      const detailsUrl = this.getTrimmedText(vendorProduct?.metadata?.payrollAdminDetailsUrl);

      let approvalStatus: CompanyGroupProductOnboardingStatus | undefined;
      let approvalLabel = '';
      let approvalDisabledReason = '';
      let canRequestApproval = false;

      if (requiresApproval) {
        approvalStatus = onboardingRow?.status || 'not_requested';
        if (approvalStatus === 'ready') {
          approvalLabel = 'Ready';
        } else if (approvalStatus === 'pending') {
          approvalLabel = 'Awaiting Wallit Confirmation';
          approvalDisabledReason =
            'Wallit is collecting the required setup details before this plan can be enabled for teams and employees.';
        } else {
          canRequestApproval = true;
          approvalLabel = matchingGroupProduct
            ? 'Requires Approval Before Enrollment Setup'
            : 'Requires Approval Before Availability';
          approvalDisabledReason = matchingGroupProduct
            ? 'Wallit must collect additional enrollment details before this plan can be enabled for teams and employees.'
            : 'Wallit approval is required before this plan can be enabled for teams and employees.';
        }
      }

      return {
        id: productId,
        stripeProductId,
        vendorName: displayNames.vendorName,
        productName: displayNames.productName,
        description,
        imageUrl,
        detailsUrl,
        mapping,
        plans,
        payrollDeductionAllowed,
        payrollDeductionEnabled: payrollDeductionAllowed && !!payrollDeductionConfig?.deductionCode,
        payrollDeductionCode: payrollDeductionConfig?.deductionCode || '',
        payrollDeductionCodeDescription: payrollDeductionConfig?.deductionCodeDescription || '',
        requiresApproval,
        approvalStatus,
        approvalLabel,
        approvalDisabledReason,
        canRequestApproval,
      };
    } catch (error: any) {
      // Deduction setup must not treat a failed catalog read as an empty/partial catalog.
      if (options.requireCompleteProducts) { throw error; }
      return null;
    }
  }

  private async loadAssignableProduct(
    productId: string,
    options: BenefitProductLoadOptions = {
      includeStripeProductEnrichment: true,
      loadVendorProductById: (id: string) => this.vendorProductsService.getVendorProductById(id),
    },
  ): Promise<AssignableBenefitProductItem | null> {
    try {
      const vendorProduct: any = await options.loadVendorProductById(productId);
      const { vendorName, productName } = this.getPayrollAdminDisplayNames(vendorProduct, productId);
      const label = `${vendorName ? `${vendorName} - ` : ''}${productName}`;
      return {
        id: productId,
        vendorName,
        productName,
        label,
      };
    } catch {
      return {
        id: productId,
        vendorName: '',
        productName: productId,
        label: productId,
      };
    }
  }

  private createVendorProductRequestCache(): (productId: string) => Promise<any> {
    const requestsByProductId = new Map<string, Promise<any>>();

    return (productId: string): Promise<any> => {
      const normalizedProductId = `${productId || ''}`.trim();
      if (!normalizedProductId) {
        return Promise.reject(new Error('Missing vendor product id'));
      }

      const existingRequest = requestsByProductId.get(normalizedProductId);
      if (existingRequest) {
        return existingRequest;
      }

      const request = this.vendorProductsService.getVendorProductById(normalizedProductId);
      requestsByProductId.set(normalizedProductId, request);
      return request;
    };
  }

  private async getCompanyLineage(company: any): Promise<any[]> {
    const lineage: any[] = [];
    let currentCompany = company;

    while (currentCompany?.id) {
      lineage.push(currentCompany);
      currentCompany = currentCompany?.parentId
        ? await this.teamCompaniesService.getCompany(currentCompany.parentId).catch(() => undefined)
        : undefined;
    }

    return lineage;
  }

  private getBestMatchingVendorGroupRate(
    vendorGroupRates: VendorGroupRateRecord[],
    vendorGroupProductIds: string[],
    companyLineageIds: string[],
  ): VendorGroupRateRecord | null {
    const normalizedGroupProductIds = new Set(this.normalizeStringArray(vendorGroupProductIds));
    if (!normalizedGroupProductIds.size || !companyLineageIds.length) {
      return null;
    }

    for (const companyId of companyLineageIds) {
      const matchedRate = (vendorGroupRates || []).find((rate) => {
        if (
          !rate?.id
          || rate?.disabled
          || rate?.metadata?.disabled
          || !normalizedGroupProductIds.has(`${rate?.vendorProductId || ''}`.trim())
        ) {
          return false;
        }
        const rateCompanyIds = this.normalizeStringArray(rate?.companyIds || []);
        return rateCompanyIds.includes(companyId);
      });
      if (matchedRate) {
        return matchedRate;
      }
    }

    return (vendorGroupRates || []).find((rate) => (
      !!rate?.id
      && !rate?.disabled
      && !rate?.metadata?.disabled
      && rate?.metadata?.targetScope === 'global'
      && normalizedGroupProductIds.has(`${rate?.vendorProductId || ''}`.trim())
    )) || null;
  }

  private getPayrollAdminDisplayNames(vendorProduct: any, fallbackProductName: string = ''): PayrollAdminDisplayNames {
    const metadata = vendorProduct?.metadata || {};
    const payrollAdminVendorName = this.getTrimmedText(metadata?.payrollAdminVendorName);
    const payrollAdminProductName = this.getTrimmedText(metadata?.payrollAdminProductName);

    return {
      vendorName: payrollAdminVendorName || this.getTrimmedText(vendorProduct?.vendorName),
      productName: payrollAdminProductName || this.getTrimmedText(vendorProduct?.productName) || fallbackProductName,
    };
  }

  private resolveBenefitProductImageUrl(vendorProduct: any, stripeProduct?: any): string {
    const metadata = vendorProduct?.metadata || {};
    const candidates = [
      ...this.getImageUrlCandidates([
        metadata?.payrollAdminImageUrl,
        metadata?.payrollAdminLogoUrl,
      ]),
      ...this.getImageUrlCandidates([
        vendorProduct?.imageUrl,
        vendorProduct?.logoUrl,
        vendorProduct?.iconUrl,
        vendorProduct?.avatarUrl,
        vendorProduct?.lightLogoUrl,
        vendorProduct?.horizontalLogoUrl,
        metadata?.imageUrl,
        metadata?.logoUrl,
        metadata?.iconUrl,
        metadata?.avatarUrl,
        metadata?.lightLogoUrl,
        metadata?.horizontalLogoUrl,
        metadata?.image?.url,
        metadata?.logo?.url,
        metadata?.icon?.url,
      ]),
      ...this.getImageUrlCandidates(stripeProduct?.images),
    ];

    return candidates[0] || '';
  }

  private getImageUrlCandidates(value: any): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((candidate) => this.getTrimmedText(candidate))
      .filter((candidate) => !!candidate);
  }

  private getTrimmedText(value: any): string {
    return `${value ?? ''}`.trim();
  }

  private buildRateDerivedBenefitProductPlanItems(
    productId: string,
    rateTable: any,
    groupedPlanItems: BenefitProductPlanItem[],
  ): BenefitProductPlanItem[] {
    const bestChoiceCandidate = this.getBestPlanLevelChoiceCandidate(rateTable);
    if (!bestChoiceCandidate || bestChoiceCandidate.options.length === 0) {
      return [];
    }

    const coverageTiers = this.getRateTableCoverageTiers(rateTable);
    return bestChoiceCandidate.options.map((option, index) => {
      const enrichment = this.findMatchingGroupedPlanItem(option.label, groupedPlanItems);
      const planLabel = `${option.label || ''}`.trim();
      return {
        id: `plan-label:${productId}:${this.toSelectionKeySegment(planLabel)}`,
        selectionMode: 'plan-label',
        selectionValues: [planLabel],
        planIds: [],
        planLabels: [planLabel],
        name: planLabel,
        description: enrichment?.description || '',
        detailsUrl: enrichment?.detailsUrl || '',
        tier: '',
        coverageTiers,
        sequence: enrichment?.sequence ?? index,
      };
    });
  }

  private getRateTableCoverageTiers(rateTable: any): string[] {
    const compactTiers = Array.isArray(rateTable?.compact?.tiers)
      ? rateTable.compact.tiers
      : [];
    const tableTiers = Array.isArray(rateTable?.tiers)
      ? rateTable.tiers
      : [];
    return this.normalizeStringArray([
      ...compactTiers.map((tier: any) => tier?.tier),
      ...tableTiers.map((tier: any) => tier?.tier),
    ]);
  }

  private getBestPlanLevelChoiceCandidate(rateTable: any): RateTableChoiceCandidate | null {
    const choiceCandidates = this.getRateTableChoiceCandidates(rateTable)
      .filter((candidate) => candidate.options.length > 0);
    if (!choiceCandidates.length) {
      return null;
    }

    const sortedCandidates = [...choiceCandidates].sort((left, right) => {
      const scoreDelta = this.getPlanLevelChoiceCandidateScore(right) - this.getPlanLevelChoiceCandidateScore(left);
      if (scoreDelta !== 0) {
        return scoreDelta;
      }
      return right.options.length - left.options.length;
    });

    const bestCandidate = sortedCandidates[0];
    return this.getPlanLevelChoiceCandidateScore(bestCandidate) > 0 ? bestCandidate : null;
  }

  private getRateTableChoiceCandidates(rateTable: any): RateTableChoiceCandidate[] {
    const candidates = new Map<string, RateTableChoiceCandidate>();
    const compactPlans = Array.isArray(rateTable?.compact?.plans) ? rateTable.compact.plans : [];

    compactPlans.forEach((plan: any) => {
      const name = `${plan?.name || ''}`.trim();
      const field = `${plan?.field || plan?.name || ''}`.trim();
      const label = `${plan?.label || plan?.name || field}`.trim();
      const values = this.expandCompactPlanValues(plan);
      if (!name || !field || values.length === 0) {
        return;
      }
      const candidateKey = `${name}::${field}`;
      const existingCandidate = candidates.get(candidateKey) || {
        name,
        field,
        label,
        options: [],
      };
      values.forEach((option) => {
        if (!existingCandidate.options.some((existingOption) => existingOption.label === option.label)) {
          existingCandidate.options.push(option);
        }
      });
      candidates.set(candidateKey, existingCandidate);
    });

    const tiers = Array.isArray(rateTable?.tiers) ? rateTable.tiers : [];
    tiers.forEach((tier: any) => {
      const rates = Array.isArray(tier?.rates) ? tier.rates : [];
      rates.forEach((rate: any) => {
        const values = Array.isArray(rate?.values) ? rate.values : [];
        values.forEach((value: any) => {
          const name = `${value?.name || ''}`.trim();
          const field = `${value?.field || name}`.trim();
          const label = `${value?.planLabel || rate?.planLabel || name}`.trim();
          const optionLabel = `${value?.label || ''}`.trim();
          if (!name || !field || !optionLabel) {
            return;
          }
          const candidateKey = `${name}::${field}`;
          const existingCandidate = candidates.get(candidateKey) || {
            name,
            field,
            label,
            options: [],
          };
          if (!existingCandidate.options.some((existingOption) => existingOption.label === optionLabel)) {
            existingCandidate.options.push({
              label: optionLabel,
              values: value?.values,
            });
          }
          candidates.set(candidateKey, existingCandidate);
        });
      });
    });

    return Array.from(candidates.values());
  }

  private expandCompactPlanValues(plan: any): RateTableChoiceOption[] {
    if (!plan) {
      return [];
    }

    const rawValues = plan?.type === 'increment'
      ? this.expandIncrementValues(plan)
      : Array.isArray(plan?.values)
        ? [...plan.values]
        : [];

    return rawValues
      .map((rawValue: any, index: number): RateTableChoiceOption | null => {
        const resolvedLabel = this.getCompactPlanDisplayLabel(rawValue);
        const optionLabel = plan?.format === 'currency' && typeof resolvedLabel === 'number'
          ? `$${resolvedLabel.toLocaleString()}`
          : `${resolvedLabel ?? ''}`.trim();
        if (!optionLabel) {
          return null;
        }

        const optionValues: Record<string, any> = {
          [`${plan?.field || plan?.name || 'value'}`.trim()]: rawValue,
        };
        (plan?.addedValues || []).forEach((addedValue: any) => {
          const addedName = `${addedValue?.name || 'value'}`.trim();
          if (!addedName) {
            return;
          }
          const addedValues = addedValue?.type === 'increment'
            ? this.expandIncrementValues(addedValue)
            : Array.isArray(addedValue?.values)
              ? [...addedValue.values]
              : [];
          optionValues[addedName] = addedValues[index] ?? null;
        });
        if (plan?.planValues && typeof plan.planValues === 'object') {
          Object.assign(optionValues, plan.planValues);
        }

        return {
          label: optionLabel,
          values: optionValues,
        };
      })
      .filter((option): option is RateTableChoiceOption => !!option);
  }

  private expandIncrementValues(config: any): number[] {
    const low = Number(config?.low);
    const high = Number(config?.high);
    const increment = Number(config?.increment);
    if (!Number.isFinite(low) || !Number.isFinite(high) || !Number.isFinite(increment) || increment <= 0) {
      return [];
    }

    const values: number[] = [];
    for (let currentValue = low; currentValue <= high; currentValue += increment) {
      values.push(currentValue);
    }
    return values;
  }

  private getCompactPlanDisplayLabel(value: any): any {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    if (Object.prototype.hasOwnProperty.call(value, 'default')) {
      return value.default;
    }

    const stateEntries = Object.entries(value)
      .filter(([key]) => key.toLowerCase() !== 'default' && /^[a-z]{2}$/i.test(key));
    if (stateEntries.length > 0) {
      return stateEntries[0][1];
    }

    return value;
  }

  private getPlanLevelChoiceCandidateScore(candidate: RateTableChoiceCandidate): number {
    const normalizedName = `${candidate?.name || ''}`.trim().toLowerCase();
    const normalizedField = `${candidate?.field || ''}`.trim().toLowerCase();
    const normalizedLabel = `${candidate?.label || ''}`.trim().toLowerCase();
    const normalizedOptionLabels = candidate.options.map((option) => `${option?.label || ''}`.trim().toLowerCase());

    if (!normalizedOptionLabels.length || normalizedOptionLabels.every((label) => this.isCoverageLevelLabel(label))) {
      return -1000;
    }

    let score = 0;
    if (normalizedLabel.includes('plan level')) {
      score += 120;
    }
    if (normalizedLabel.includes('plan')) {
      score += 80;
    }
    if (normalizedName.includes('primaryplan') || normalizedField.includes('primaryplan')) {
      score += 70;
    }
    if (normalizedName.includes('plan') || normalizedField.includes('plan')) {
      score += 60;
    }
    if (candidate.options.some((option) => this.optionValuesLookPlanLike(option.values))) {
      score += 40;
    }
    if (candidate.options.length > 1) {
      score += 20;
    }
    score += Math.min(candidate.options.length, 10);

    return score;
  }

  private optionValuesLookPlanLike(values: Record<string, any> | undefined): boolean {
    const normalizedKeys = Object.keys(values || {})
      .map((key) => `${key || ''}`.trim().toLowerCase());
    return normalizedKeys.some((key) => (
      key.includes('plan')
      || key.includes('code')
      || key.includes('package')
      || key.includes('benefit')
    ));
  }

  private isCoverageLevelLabel(label: string): boolean {
    const normalizedLabel = `${label || ''}`.trim().toLowerCase();
    return [
      'member only',
      'member plus spouse',
      'member plus children',
      'member plus family',
      'member plus one',
      'employee only',
      'employee plus spouse',
      'employee plus children',
      'employee plus family',
      'employee plus one',
    ].includes(normalizedLabel);
  }

  private findMatchingGroupedPlanItem(
    optionLabel: string,
    groupedPlanItems: BenefitProductPlanItem[],
  ): BenefitProductPlanItem | null {
    const normalizedOptionLabel = this.normalizePlanLevelMatchText(optionLabel);
    if (!normalizedOptionLabel) {
      return null;
    }

    return groupedPlanItems.find((planItem) => (
      this.normalizePlanLevelMatchText(planItem.name) === normalizedOptionLabel
    )) || groupedPlanItems.find((planItem) => {
      const normalizedPlanName = this.normalizePlanLevelMatchText(planItem.name);
      return normalizedPlanName.endsWith(normalizedOptionLabel)
        || normalizedPlanName.includes(normalizedOptionLabel)
        || normalizedOptionLabel.includes(normalizedPlanName);
    }) || null;
  }

  private normalizePlanLevelMatchText(value: string): string {
    return `${value || ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(plan|ppo|dental|vision|delta)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toSelectionKeySegment(value: string): string {
    return `${value || ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

}
